'use client';

import styled from 'styled-components';
import DataAccordion, {
  DataAccordionContent,
} from '@dvd/components/DataAccordion';
import * as Styled from '@lib/styles';
import { ImageProps } from '@types';

type SharedDataAccordionProps = {
  /**
   *
   * Headline that summarizes the data shared with the external integration
   */
  headline: string;

  /**
   * Description about the data shared with the external integration
   */
  description: string;

  /**
   * Logomark image of the the target (external) integration title on each data accordion.
   *
   * Rules:
   * - Alt text for the logomark should be the name of the platform/service
   * - Logomarks have a width & height of 20x20
   * - Non-square logos may need to be smaller than 20x20 so they aren't cut off in image derivatives created by our image optimization
   */
  targetImage: ImageProps;

  /**
   * List of data accordions that detail an integration and its' mapped fields
   *
   * Reference the DataAccordion component to see the props used dataAccordions.
   * The target logomark is not listed here — it is the same for every entry,
   * so it comes from `targetImage` above.
   */
  dataAccordions: DataAccordionContent[];
};

const SharedDataAccordionContainer = styled.div`
  padding-block: ${({ theme }) => theme.spacing[3]};

  ${({ theme }) => theme.mq.minWidth.medium} {
    padding-block: ${({ theme }) => theme.spacing[4]};
  }
`;

const Headline = styled.h3`
  margin-bottom: ${({ theme }) => theme.spacing['0.75']};

  ${({ theme }) => theme.mq.minWidth.medium} {
    margin-bottom: ${({ theme }) => theme.spacing['1']};
  }
`;

const Description = styled(Styled.BodyMd)`
  margin-bottom: ${({ theme }) => theme.spacing['1.5']};

  ${({ theme }) => theme.mq.minWidth.medium} {
    margin-bottom: ${({ theme }) => theme.spacing['2']};
  }
`;

const DataAccordionList = styled.ul`
  list-style-type: none;
  padding: 0;
  margin: 0;

  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing['1.5']};

  ${({ theme }) => theme.mq.minWidth.medium} {
    gap: ${({ theme }) => theme.spacing['2']};
  }
`;

/**
 * Displays a list of data accordions below a heading and description.
 *
 * Used on integration pages.
 */
const SharedDataAccordion = ({
  headline,
  description,
  targetImage,
  dataAccordions,
}: SharedDataAccordionProps) => {
  return (
    <SharedDataAccordionContainer>
      <Headline>{headline}</Headline>
      <Description>{description}</Description>
      <DataAccordionList>
        {dataAccordions.map(
          (
            {
              sourceTitle,
              targetTitle,
              headline,
              description,
              rows,
              tags,
              defaultOpen,
            },
            index,
          ) => {
            return (
              <DataAccordion
                key={index}
                sourceTitle={sourceTitle}
                targetTitle={targetTitle}
                headline={headline}
                description={description}
                rows={rows}
                targetImage={targetImage}
                tags={tags}
                defaultOpen={defaultOpen}
              />
            );
          },
        )}
      </DataAccordionList>
    </SharedDataAccordionContainer>
  );
};

export default SharedDataAccordion;
